import { Col, Container, Row } from 'react-bootstrap'
import { IntegrationServiceId } from '../../../../../../../common/integrations'
import { useGetIntegrationAdminSettings } from '../../../hook'
import { useZocksQuota } from '../../../../../../../components/ScopedBlock/hooks'
import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import React from 'react'
import { CustomAdditionalObjectsAdminSettings } from './CustomAdditionalObjectsAdminSettings'
import { IntegrationMappingTesterAccordion } from '../../../../TesterTool/TesterAccordion'
import { AdditionalObjectsMappingTester } from '../../../../TesterTool/AdditionalObjects/AdditionalObjectsMappingTester'

interface AdditionalObjectsAdminSettingsProps {
  serviceId: IntegrationServiceId
}

export const AdditionalObjectsAdminSettings: React.FC<AdditionalObjectsAdminSettingsProps> = ({ serviceId }) => {
  const { isAccountFetching } = useGetIntegrationAdminSettings(serviceId)
  const { getQuotaValue } = useZocksQuota()
  const additionalObjectsLimit = getQuotaValue('integration.additionalObjMax.value')
  const {
    watch,
    formState: { errors },
  } = useFormContext<IntegrationSettings>()

  const additionalObjects = watch('mappings.additionalObjects')

  const sumAdditionalObjectsCount = additionalObjects ? additionalObjects.map((obj) => obj?.limit || 0).reduce((a, b) => a + b, 0) : 0

  const additionalObjectsError = errors.mappings?.additionalObjects

  if (isAccountFetching) {
    return (
      <Container>
        <div className='skeleton pt-2' style={{ width: '100%', height: '60px' }} />
        <div className='skeleton pt-2' style={{ width: '100%', height: '60px' }} />
      </Container>
    )
  }

  return (
    <Container>
      <Row className='mb-5'>
        <Col>
          <h3 className='mb-2'>AI Context Objects</h3>
          <div className='zocks-text-gray-600'>
            <p>
              Configure which Salesforce objects your AI Assistant can access for context. Zocks pulls records from these objects to generate Meeting Prep,
              provide informed insights and more. Adding more objects gives the AI a deeper understanding of your CRM data and enables more accurate responses.
            </p>
            <p>For each object, you can specify how many records to include. The total limit across all objects is {additionalObjectsLimit} records.</p>
          </div>
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col className='zocks-bg-color-gray-100 zocks-border-0.5 p-4'>
          <div className='d-flex justify-content-between'>
            <div>
              <h5>Total Limit</h5>
              <div>
                Configure how many historical records Zocks should access from each object. Maximum of {additionalObjectsLimit} records combined across all
                objects.
              </div>
            </div>
            <div className='d-flex gap-3 align-items-center'>
              <div className='py-2 zocks-px-0.75 zocks-border-0.25 zocks-bg-color-gray-200'>
                <b>{sumAdditionalObjectsCount}</b>
              </div>
              <div>/ {additionalObjectsLimit}</div>
            </div>
          </div>
          {additionalObjectsError && <div className='zocks-text-danger mt-2'>{additionalObjectsError.message}</div>}
        </Col>
      </Row>
      <Row>
        <CustomAdditionalObjectsAdminSettings serviceId={serviceId} />
      </Row>
      <Row className='mt-5'>
        <IntegrationMappingTesterAccordion serviceId={serviceId} testerComponents={[AdditionalObjectsMappingTester]} title='Test AI Context' />
      </Row>
    </Container>
  )
}
